import { createOpenAI } from '@ai-sdk/openai';

export const defaultNutritionTranscriptionModel = 'gpt-4o-mini-transcribe';

export const nutritionVoiceTranscriptionPrompt = [
  'This audio is a food logging note.',
  'Preserve food names, brands, quantities, units, and meal details as spoken.',
  'Do not summarize or infer nutrition values.',
].join(' ');

export type NutritionAudioTranscriptionProvider = 'browser-fallback' | 'openai';

export interface NutritionAudioTranscriptionRequest {
  audioBase64: string;
  browserTranscript?: string;
  contentType: string;
  fileName: string;
  language?: string;
}

export interface NutritionAudioTranscriptionResult {
  durationSeconds?: number;
  language?: string;
  provider: NutritionAudioTranscriptionProvider;
  transcript: string;
  warning: string | null;
}

export interface NutritionAudioTranscriptionRuntime {
  openAiApiKey?: string;
  transcriptionModel?: string;
}

interface TranscribeAudioImplementation {
  (input: {
    audio: Uint8Array;
    mediaType: string;
    model: TranscriptionModel;
    providerOptions?: {
      openai?: {
        language?: string;
        prompt?: string;
      };
    };
  }): Promise<{
    durationInSeconds?: number | undefined;
    language?: string | undefined;
    text: string;
  }>;
}

interface TranscriptionModel {
  doGenerate(input: {
    audio: Uint8Array;
    mediaType: string;
    providerOptions?: {
      openai?: {
        language?: string;
        prompt?: string;
      };
    };
  }): PromiseLike<{
    durationInSeconds?: number | undefined;
    language?: string | undefined;
    text: string;
  }>;
  modelId: string;
}

export function normalizeTranscriptText(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function decodeBase64Audio(value: string): Uint8Array {
  const sanitizedValue = value.replace(/^data:[^;]+;base64,/, '').trim();

  if (sanitizedValue.length === 0) {
    throw new Error('Audio data was empty.');
  }

  if (typeof Buffer !== 'undefined') {
    return Uint8Array.from(Buffer.from(sanitizedValue, 'base64'));
  }

  const binaryValue = atob(sanitizedValue);
  const bytes = new Uint8Array(binaryValue.length);

  for (let index = 0; index < binaryValue.length; index += 1) {
    bytes[index] = binaryValue.charCodeAt(index);
  }

  return bytes;
}

export function normalizeAudioContentType(value: string): string {
  const normalizedValue = value.split(';', 1)[0]?.trim().toLowerCase();

  return normalizedValue && normalizedValue.length > 0 ? normalizedValue : 'audio/webm';
}

async function transcribeWithModel(input: {
  audio: Uint8Array;
  mediaType: string;
  model: TranscriptionModel;
  providerOptions?: {
    openai?: {
      language?: string;
      prompt?: string;
    };
  };
}) {
  const request: Parameters<TranscriptionModel['doGenerate']>[0] = {
    audio: input.audio,
    mediaType: input.mediaType,
  };

  if (input.providerOptions) {
    request.providerOptions = input.providerOptions;
  }

  return input.model.doGenerate(request);
}

function createFallbackTranscriptResult(
  transcript: string,
  warning: string,
): NutritionAudioTranscriptionResult {
  return {
    transcript,
    provider: 'browser-fallback',
    warning,
  };
}

export async function transcribeNutritionAudio(input: {
  request: NutritionAudioTranscriptionRequest;
  runtime?: NutritionAudioTranscriptionRuntime;
  transcribe?: TranscribeAudioImplementation;
}): Promise<NutritionAudioTranscriptionResult> {
  const browserTranscript = normalizeTranscriptText(input.request.browserTranscript ?? '');
  const apiKey = input.runtime?.openAiApiKey?.trim();

  if (!apiKey) {
    if (browserTranscript.length > 0) {
      return createFallbackTranscriptResult(
        browserTranscript,
        'Using the browser transcript because server transcription is not configured.',
      );
    }

    throw new Error('Audio transcription is not configured on the server.');
  }

  const openai = createOpenAI({
    apiKey,
  });
  const implementation = input.transcribe ?? transcribeWithModel;

  try {
    const openAiProviderOptions: {
      language?: string;
      prompt?: string;
    } = {
      prompt: nutritionVoiceTranscriptionPrompt,
    };

    if (input.request.language) {
      openAiProviderOptions.language = input.request.language;
    }

    const result = await implementation({
      audio: decodeBase64Audio(input.request.audioBase64),
      mediaType: normalizeAudioContentType(input.request.contentType),
      model: openai.transcription(
        input.runtime?.transcriptionModel?.trim() || defaultNutritionTranscriptionModel,
      ),
      providerOptions: {
        openai: openAiProviderOptions,
      },
    });
    const transcript = normalizeTranscriptText(result.text);

    if (transcript.length === 0) {
      throw new Error(`No transcript text was returned for ${input.request.fileName}.`);
    }

    const transcriptionResult: NutritionAudioTranscriptionResult = {
      transcript,
      provider: 'openai',
      warning: null,
    };

    if (result.language) {
      transcriptionResult.language = result.language;
    }

    if (typeof result.durationInSeconds === 'number') {
      transcriptionResult.durationSeconds = result.durationInSeconds;
    }

    return transcriptionResult;
  } catch (error) {
    if (browserTranscript.length > 0) {
      return createFallbackTranscriptResult(
        browserTranscript,
        'Using the browser transcript because server transcription was unavailable.',
      );
    }

    if (error instanceof Error && error.message.length > 0) {
      throw error;
    }

    throw new Error('Audio transcription failed.');
  }
}
